import { AccountDetails } from './AccountDetails';

const AccountDetailsKey = "DS_ACCOUNT_DETAILS_KEY";

type AccountListener = (accountDetails: AccountDetails | null) => void;

const listeners = new Set<AccountListener>();

/**
 * The cached account is the app's single notion of "who is connected", so
 * every part of the UI can follow it from here rather than each component
 * checking the wallet on its own and drifting out of step.
 */
export function subscribeToCachedAccount(listener: AccountListener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function notify(accountDetails: AccountDetails | null) {
    listeners.forEach((listener) => listener(accountDetails));
}

export function setCachedAccountDetails(accountDetails: AccountDetails) {
    try {
        localStorage.setItem(AccountDetailsKey, JSON.stringify(accountDetails));
    } catch (error) {
        console.log("[LocalStorage] Could not cache account details: " + error);
    }
    notify(accountDetails);
}

export function fetchCachedAccountDetails(): AccountDetails | null {
    let raw: string | null;
    try {
        raw = localStorage.getItem(AccountDetailsKey);
    } catch (error) {
        console.log("[LocalStorage] Could not read cached account details: " + error);
        return null;
    }

    if (raw === null) {
        return null;
    }

    let cached: Partial<AccountDetails> | null;
    try {
        cached = JSON.parse(raw);
    } catch {
        clearCachedAccountDetails();
        return null;
    }

    if (!cached
        || cached.shortenedAddress === undefined
        || cached.fullAddress === undefined
        || cached.weiBalance === undefined
        || cached.displayBalance === undefined) {
        console.log("[LocalStorage] Cached account details incomplete, discarding.");
        clearCachedAccountDetails();
        return null;
    }

    return new AccountDetails(cached.shortenedAddress, cached.fullAddress, cached.weiBalance, cached.displayBalance);
}

export function clearCachedAccountDetails() {
    try {
        localStorage.removeItem(AccountDetailsKey);
    } catch {
        // Nothing we can do if storage is unavailable.
    }
    notify(null);
}
